import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency } from '@/utils/gameLogic';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  Bell,
  Check,
  Clock,
  Gift,
  Hotel,
  Mail,
  MessageSquare,
  Phone,
  Plane,
  Train,
  X
} from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';

export const DecisionPanel = ({
  customer,
  transportOptions,
  communicationOptions,
  accommodationOptions = [],
  compensationOptions = [],
  selectedDecisions,
  onSelectDecision,
  onConfirm,
  onCancel,
  budget
}) => {
  const [activeTab, setActiveTab] = useState('transport');

  // Always add "Apology Only" as the first option in compensation list
  // Filter out any existing apology options from JSON to avoid duplicates
  const filteredCompensationOptions = compensationOptions.filter(opt => 
    opt.id !== 'apology' && !opt.name?.toLowerCase().includes('apology')
  );
  const allCompensationOptions = [
    {
      id: 'apology',
      name: 'Apology only',
      cost: 0,
      csat: 0,
      recommended: false,
      details: {
        type: 'apology',
        value: 0,
        description: 'Verbal apology'
      }
    },
    ...filteredCompensationOptions
  ];

  const getTotalCost = useCallback(() => {
    let total = 0;
    if (selectedDecisions.transport) total += selectedDecisions.transport.cost;
    // Communication has no cost
    if (selectedDecisions.accommodation) total += selectedDecisions.accommodation.cost;
    if (selectedDecisions.compensation) total += selectedDecisions.compensation.cost;
    return total;
  }, [selectedDecisions]);

  const isOptionSelected = (category, optionId) => {
    return selectedDecisions[category]?.id === optionId;
  };

  const canConfirm = useCallback(() => {
    return selectedDecisions.transport && selectedDecisions.communication;
  }, [selectedDecisions]);

  // Handle Enter key to confirm solution
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        // Don't trigger if user is typing in an input field
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
          return;
        }
        
        if (canConfirm() && getTotalCost() <= budget) {
          event.preventDefault();
          onConfirm();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedDecisions, budget, onConfirm, canConfirm, getTotalCost]);

  const getChannelIcon = (channel) => {
    switch(channel) {
      case 'call': return <Phone className="w-4 h-4" />;
      case 'sms': return <MessageSquare className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'push': return <Bell className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getChannelName = (channel) => {
    switch(channel) {
      case 'call': return 'Phone Call';
      case 'sms': return 'SMS';
      case 'email': return 'Email';
      case 'push': return 'Push Notification';
      default: return channel;
    }
  };

  const getToneName = (tone) => {
    switch(tone) {
      case 'empathetic': return 'Empathetic';
      case 'business': return 'Business';
      case 'minimal': return 'Minimal';
      default: return tone;
    }
  };


  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <Card className="w-full max-w-5xl h-[85vh] flex flex-col">
        <CardHeader className="border-b py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <CardTitle className="text-xl">{customer.profile.name} • {customer.profile.tier}</CardTitle>
              <div className="mt-1 space-y-0.5">
                <CardDescription className="text-sm flex items-center gap-2">
                  <span className="font-semibold">{customer.trip.from} → {customer.trip.to}</span>
                  <span>•</span>
                  <span>Original: {customer.trip.original.flight} • {customer.trip.original.departs} - {customer.trip.original.arrives} • {customer.trip.original.class}</span>
                </CardDescription>
                <CardDescription className="text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  {customer.trip.purpose}
                </CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onCancel} className="h-8 w-8">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Budget Info */}
          <div className="mt-2 p-3 bg-muted rounded flex items-center justify-between text-base">
            <div>
              <span className="text-muted-foreground">Budget:</span>
              <span className="font-mono font-bold ml-1">{formatCurrency(budget)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Selected:</span>
              <span className={`font-mono font-bold ml-1 ${
                getTotalCost() > budget ? 'text-[hsl(var(--critical))]' : 'text-[hsl(var(--normal))]'
              }`}>
                {formatCurrency(getTotalCost())}
              </span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden p-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className="grid w-full grid-cols-4 h-11">
              <TabsTrigger value="transport" className="relative text-sm">
                <Plane className="w-4 h-4 mr-1" />
                Transport
                {selectedDecisions.transport && (
                  <Check className="w-3 h-3 absolute top-0.5 right-0.5 text-[hsl(var(--normal))]" />
                )}
              </TabsTrigger>
              <TabsTrigger
                value="communication"
                className="relative text-sm"
                disabled={!selectedDecisions.transport}
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Communication
                {selectedDecisions.communication && (
                  <Check className="w-3 h-3 absolute top-0.5 right-0.5 text-[hsl(var(--normal))]" />
                )}
              </TabsTrigger>
              <TabsTrigger value="accommodation" className="relative text-sm">
                <Hotel className="w-4 h-4 mr-1" />
                Hotel
                {selectedDecisions.accommodation && (
                  <Check className="w-3 h-3 absolute top-0.5 right-0.5 text-[hsl(var(--normal))]" />
                )}
              </TabsTrigger>
              <TabsTrigger value="compensation" className="relative text-sm">
                <Gift className="w-4 h-4 mr-1" />
                Compensation
                {selectedDecisions.compensation && (
                  <Check className="w-3 h-3 absolute top-0.5 right-0.5 text-[hsl(var(--normal))]" />
                )}
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 mt-3">
              <TabsContent value="transport" className="space-y-2 mt-0 px-1 py-1">
                {transportOptions.map(option => {
                  // Determine transport type: default to "airplane" if not specified
                  const transportType = option.details?.type || option.type || 'airplane';
                  const TransportIcon = transportType === 'train' ? Train : Plane;
                  
                  return (
                    <OptionCard
                      key={option.id}
                      option={option}
                      isSelected={isOptionSelected('transport', option.id)}
                      onSelect={() => {
                        // Clear communication selection when transport changes
                        if (selectedDecisions.communication) {
                          onSelectDecision('communication', null);
                        }
                        onSelectDecision('transport', option);
                      }}
                      icon={<TransportIcon className="w-4 h-4" />}
                    />
                  );
                })}
              </TabsContent>

              <TabsContent value="communication" className="space-y-2 mt-0 px-1 py-1">
                {!selectedDecisions.transport ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Please select transport first</p>
                  </div>
                ) : (
                  communicationOptions.map(option => (
                    <CommunicationOptionCard
                      key={`${option.id}-${selectedDecisions.transport?.id || 'none'}`}
                      option={option}
                      selectedTransport={selectedDecisions.transport}
                      isSelected={isOptionSelected('communication', option.id)}
                      onSelect={() => {
                        // Create option with message for selected transport when selecting
                        const getMessageForTransport = () => {
                          if (!selectedDecisions.transport) return null;
                          if (option.details?.messages && typeof option.details.messages === 'object') {
                            const transportId = selectedDecisions.transport.id;
                            return option.details.messages[transportId] || Object.values(option.details.messages)[0];
                          }
                          return option.details?.message || null;
                        };

                        const message = getMessageForTransport();
                        const optionWithMessage = {
                          ...option,
                          details: {
                            ...option.details,
                            message: message
                          }
                        };
                        onSelectDecision('communication', optionWithMessage);
                      }}
                      icon={getChannelIcon(option.details.channel)}
                    />
                  ))
                )}
              </TabsContent>

              <TabsContent value="accommodation" className="space-y-2 mt-0 px-1 py-1">
                {/* Use accommodation options from JSON (No Hotel should be first in JSON) */}
                {accommodationOptions.map(option => {
                  // Use X icon for "No Hotel" option, Hotel icon for others
                  const AccommodationIcon = option.id === 'no_hotel' || option.name === 'No Hotel' 
                    ? X 
                    : Hotel;
                  
                  return (
                    <OptionCard
                      key={option.id}
                      option={option}
                      isSelected={isOptionSelected('accommodation', option.id)}
                      onSelect={() => onSelectDecision('accommodation', option)}
                      icon={<AccommodationIcon className="w-4 h-4" />}
                    />
                  );
                })}
              </TabsContent>

              <TabsContent value="compensation" className="space-y-2 mt-0 px-1 py-1">
                {allCompensationOptions.map(option => (
                  <OptionCard
                    key={option.id}
                    option={option}
                    isSelected={isOptionSelected('compensation', option.id)}
                    onSelect={() => onSelectDecision('compensation', option)}
                    icon={<Gift className="w-4 h-4" />}
                  />
                ))}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </CardContent>

        <div className="border-t p-3">
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              {!canConfirm() && 'Select Transport + Communication'}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onCancel} size="default">
                Cancel
              </Button>
              <Button
                onClick={onConfirm}
                disabled={!canConfirm() || getTotalCost() > budget}
                className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
                size="default"
              >
                Confirm Solution
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

const OptionCard = ({ option, isSelected, onSelect, icon, showMessage }) => {
  const isTransport = option.details?.departs && option.details?.arrives;

  return (
    <motion.div
      whileHover={{ scale: 1.002 }}
      whileTap={{ scale: 0.998 }}
      onClick={onSelect}
      className={`p-4 rounded-lg border cursor-pointer transition-all ${
        isSelected
          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5'
          : 'border-border hover:border-[hsl(var(--primary))]/50 hover:bg-muted/50'
      }`}
      style={{ transformOrigin: 'center' }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`p-2 rounded ${
            isSelected ? 'bg-[hsl(var(--primary))]' : 'bg-muted'
          }`}>
            {React.cloneElement(icon, { className: 'w-5 h-5' })}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-base truncate">{option.name}</h4>
              {isSelected && (
                <Check className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0" />
              )}
            </div>
            {isTransport && option.details && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">{option.details.departs}</span>
                </span>
                <span>→</span>
                <span className="font-medium">
                  {option.details.arrives}
                  {option.details.airport && ` (${option.details.airport})`}
                </span>
                {option.details.class && (
                  <>
                    <span>•</span>
                    <span className="capitalize">{option.details.class}</span>
                  </>
                )}
              </div>
            )}
            {option.details && !showMessage && !isTransport && (
              <div className="flex gap-3 text-sm text-muted-foreground mt-1">
                {Object.entries(option.details).slice(0, 3).map(([key, value]) => {
                  if (key !== 'message' && key !== 'departs' && key !== 'arrives') {
                    return (
                      <span key={key}>
                        {Array.isArray(value) ? value.join(', ') : value}
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-mono font-bold text-base">{formatCurrency(option.cost)}</p>
        </div>
      </div>

      {option.details?.message && showMessage && (
        <div className="mt-2 p-2 bg-muted/50 rounded text-sm italic">
          "{option.details.message}"
        </div>
      )}
    </motion.div>
  );
};

const CommunicationOptionCard = ({ option, isSelected, onSelect, icon, selectedTransport }) => {
  const getChannelName = (channel) => {
    switch(channel) {
      case 'call': return 'Phone Call';
      case 'sms': return 'SMS';
      case 'email': return 'Email';
      case 'push': return 'Push Notification';
      default: return channel;
    }
  };

  // Get message for selected transport dynamically - always from messages dict
  const getMessage = () => {
    if (!selectedTransport) return null;

    // Always check messages dictionary first
    if (option.details?.messages && typeof option.details.messages === 'object') {
      const transportId = selectedTransport.id;
      // Get message for this specific transport
      const message = option.details.messages[transportId];
      if (message) return message;
      // Fallback to first available message if transport not found
      const firstMessage = Object.values(option.details.messages)[0];
      return firstMessage || null;
    }

    // Fallback to old message format for backward compatibility (should not happen with new structure)
    return option.details?.message || null;
  };

  // Always get message dynamically, don't use saved message
  const message = getMessage();

  return (
    <motion.div
      whileHover={{ scale: 1.002 }}
      whileTap={{ scale: 0.998 }}
      onClick={onSelect}
      className={`p-3 rounded-lg border cursor-pointer transition-all ${
        isSelected
          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5'
          : 'border-border hover:border-[hsl(var(--primary))]/50 hover:bg-muted/50'
      }`}
      style={{ transformOrigin: 'center' }}
    >
      <div className="flex gap-3 items-center">
        {/* Left side: Icon and channel type in horizontal row with fixed width */}
        <div className="flex items-center gap-2 flex-shrink-0" style={{ width: '120px', minWidth: '120px' }}>
          <div className={`p-1.5 rounded flex-shrink-0 ${
            isSelected ? 'bg-[hsl(var(--primary))]' : 'bg-muted'
          }`}>
            {React.cloneElement(icon, { className: 'w-4 h-4' })}
          </div>
          <div className="flex flex-col items-start gap-0.5 min-w-0">
            <h4 className="font-semibold text-xs text-muted-foreground truncate w-full">
              {getChannelName(option.details.channel)}
            </h4>
            {isSelected && (
              <Check className="w-3 h-3 text-[hsl(var(--primary))] flex-shrink-0" />
            )}
          </div>
        </div>
        
        {/* Right side: Message text aligned with center */}
        {message && (
          <div className="flex-1 min-w-0">
            <p className="text-sm leading-relaxed text-foreground">
              {message}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};
